import {useQuery} from '@aboutyou/data-fetcher';

export interface ScaylePaymentOption {
  paymentOptionKey: string;
  isDisabled: boolean;
  notifications?: {
    textTranslationKey: CheckoutPaymentTranslationKey;
  }[];
}

export type CheckoutPaymentTranslationKey =
  | 'payment.notifications.constraintMinAge' // -> translationKey for the Min age constraint message.
  | 'payment.notifications.constraintMinOrderValue' // -> translationKey for the Min order value constraint message.
  | 'payment.notifications.constraintMaxOrderValue' // -> translationKey for the Max order value constraint message.
  | 'payment.notifications.errorTechnical' // -> translationKey for any error thats returned by third party providers (e.g. ZPS)
  | 'payment.notifications.paymentMethodNotAvailable' // -> translationKey for the message shown when a payment method is not available due to reasons other than an error (e.g. BNPL not available because the customer is not eligible, which is not a technical error but a business rule)
  | 'payment.notifications.errorPaymentMethodNotAvailable' // -> translationKey for the message shown when a payment method is not available due to an error (e.g. an error returned by a third party provider like ZPS)
  | 'payment.notifications.blockedForInvoice' // -> translationKey for the message shown when a payment method is blocked for invoice for a customer (e.g. due to negative invoice history)
  | 'payment.notifications.initialRiskAssessmentDenied' // -> translationKey for Risk check 1 failure
  | 'payment.notifications.riskAssessmentDenied'; // -> translationKey for Risk check 2 failure

export interface ScayleShippingOption {
  deliveryOptionKey: string;
  carrierKey: string;
  orderValueLimits?: {
    maxBruttoOrderValue?: number;
    minBruttoOrderValue?: number;
  };
}

interface ScayleBasketItem {
  id: string;
  productId: number;
  additionalData?: {
    captchaRequired?: boolean;
  };
}

interface ScayleBasketPackage {
  items: ScayleBasketItem[];
}

interface ScayleServiceCost {
  key: string;
  value: number;
  netValue: number;
}

interface ConditionalFees {
  amountWithTax: number;
  key: string;
  taxRate: number;
}

export interface ScayleState {
  isExpressCheckout?: boolean;
  orderState: {
    id: string;
    basket?: {
      id: string;
      packages: ScayleBasketPackage[];
      serviceCosts?: ScayleServiceCost[];
      total?: {
        costWithTaxWithMembershipDiscountWithoutServiceCosts?: number;
      };
    };
    payment?: {
      selected: ScaylePaymentOption[];
    };
    shipping?: {
      selected: ScayleShippingOption[];
    };
  };
  orderOptions?: {
    payment?: {
      generic: ScaylePaymentOption[];
    };
    conditionalFees?: ConditionalFees[];
    minimumOrderValue?: number;
  };
  customer: {
    data?: {
      id: number;
      trackingHash: string;
      birthDate: string;
      email: string;
      firstName: string;
      gender: string;
      lastName: string;
      phone?: {
        plainText: string;
      };
      customData?: {
        AYO_customer_type: string;
      };
    };
  };
}

export const useScayleState = () => {
  const query = useQuery<ScayleState | null>(
    'scayleState',
    async () => {
      const handleStateUpdate = (event: Event) => {
        const {state} = (event as CustomEvent).detail;
        query.updateCache(state);
      };

      // eslint-disable-next-line local-rules/window-usage
      window.addEventListener('SCAYLE_STATE_UPDATE', handleStateUpdate);

      return null;
    },
    {
      enabled: process.browser,
    },
  );
  return query;
};
